"use client";

import { useEffect, useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { Pagination } from "antd";
import Image from "next/image";
import { twMerge } from "tailwind-merge";
import { useIsDark } from "@/context/ThemeContext";
import { urlFor } from "@/sanity/lib/image";
import { searchAndFilterPaginatedUseCases } from "@/sanity/actions/useCaseActions";

import HeroSection from "@/components/common/HeroSection";
import BlogCard from "@/components/blog/BlogCard";
import { UseCaseCardSkeleton } from "@/components/skeletons/UseCaseCardSkeleton";

export default function UseCasePageClient() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const isdark = useIsDark();

  const [posts, setPosts] = useState([]);
  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
  });
  const [totalItems, setTotalItems] = useState(0);
  const [postsLoading, setPostsLoading] = useState(true);

  const currentPage = Number(searchParams.get("page")) || 1;
  const cardsPerPage = 18;

  const getImageUrl = (image, width = 800, height = 600, quality = 80) => {
    try {
      if (!image) return "/assets/fallbacks/blogFallback.webp";
      return urlFor(image)
        .width(width)
        .height(height)
        .quality(quality)
        .auto("format")
        .url();
    } catch (e) {
      return "/assets/fallbacks/blogFallback.webp";
    }
  };

  useEffect(() => {
    (async () => {
      setPostsLoading(true);
      const { posts, total } = await searchAndFilterPaginatedUseCases(
        filters,
        currentPage,
        cardsPerPage,
      );
      setPosts(posts);
      setTotalItems(total);
      setPostsLoading(false);
    })();
  }, [filters, currentPage]);

  const handleSearchChange = (value) => {
    const newFilters = { ...filters, search: value };
    setFilters(newFilters);

    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set("search", value);
    } else {
      params.delete("search");
    }
    params.set("page", "1");
    router.push(`?${params.toString()}`, { scroll: false });
  };

  return (
    <div className="relative flex flex-col justify-center items-center mt-6 mx-2 sm:mx-5 md:mx-[40px] xl:mx-[100px] 3xl:mx-[150] z-10">
      <HeroSection
        heading="SUCCESS STORIES"
        subHeading="Where Strategy Meets Technology"
        paragraph="Explore real-world examples of how organizations are using AI, analytics and cloud technologies to drive transformation, improve visibility and make smarter decisions."
        headingStyles="text-[35px] mobile:text-[50px] sm:text-[60px] md:text-[75px] tablet:text-[95px] lg:text-[105px] xl:text-[135px] laptop:text-[155px] 3xl:text-[190px] 4xl:text-[210px] leading-[100%] text-center"
        scrollTarget="useCases"
      />

      {/* Search Input Bar */}
      {/* <div id="useCases" className="mt-16 w-full max-w-[1800px] mx-auto">
        <div
          className={twMerge(
            "w-full rounded-[10px] p-[1.5px]",
            isdark
              ? "bg-gradient-to-r from-[#161616] via-white/15 to-white/15"
              : "bg-gradient-to-r from-[#E28EF3] to-[#EEEEEE]"
          )}
        >
          <div
            className={twMerge(
              "w-full rounded-[10px] p-4 flex items-center justify-between",
              isdark ? "bg-[#181A20] text-secondary" : "bg-[#FFFFFF] text-secondary"
            )}
          >
            <div className="relative w-full">
              <Image
                src="/assets/blog/searchIcon.svg"
                alt="search"
                width={18}
                height={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 opacity-70 pointer-events-none"
              />

              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="search use cases...."
                className={twMerge(
                  "w-full h-[40px] rounded-[36px] pl-10 pr-4 font-[Manrope] font-medium text-[14px] xl:text-[16px] 3xl:text-[18px] leading-[20px] tracking-[0.1px] align-middle focus:outline-none placeholder:text-gray-500",
                  isdark
                    ? "bg-[#181A20] border border-[#818284] text-secondary"
                    : "bg-[#FFFFFF] border border-[#1A1A1A80] text-secondary"
                )}
              />
            </div>
          </div>
        </div>
      </div> */}

      {/* Grid of cards */}
      <div className="mt-16 w-full">
        <div className="grid grid-cols-1 mobile:grid-cols-2 md:grid-cols-3 tablet:grid-cols-3 gap-4 mobile:gap-3 sm:gap-4 md:gap-3 tablet:gap-6 lg:gap-10 2xl:gap-20 3xl:gap-30 max-w-[1800px] mx-auto">
          {postsLoading ? (
            <>
              {/* Cards Skeleton */}
              {Array.from({ length: 6 }).map((_, i) => (
                <UseCaseCardSkeleton key={i} />
              ))}
            </>
          ) : (
            posts.map((card) => (
              <BlogCard
                key={card._id}
                image={getImageUrl(card.mainImage, 370, 173, 80)}
                blurDataURL={card.mainImage?.asset?.metadata?.lqip}
                // date={
                //   card.publishedAt
                //     ? new Date(card.publishedAt).toDateString()
                //     : ""
                // }
                title={card.title}
                description={card.excerpt || ""}
                slug={card.slug}
                tags={card.tags?.map((t) => t.title) || []}
                basePath="/successStories"
                showAuthor={false}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex justify-center mt-10 mb-10 px-2">
        {totalItems > cardsPerPage && (
          <Pagination
            current={currentPage}
            pageSize={cardsPerPage}
            total={totalItems}
            onChange={(page) => {
              const params = new URLSearchParams(searchParams.toString());
              params.set("page", page.toString());
              router.push(`?${params.toString()}`);
            }}
            showSizeChanger={false}
            className="custom-pagination"
          />
        )}
      </div>
    </div>
  );
}
